import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

import { getStorage, ref as storageRef, listAll } from 'firebase/storage';
import { getDatabase, ref, onValue, remove, set, get as getValue, update } from 'firebase/database';
import { getFunctions, httpsCallable } from "firebase/functions";

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { loadMatchData } from '../../cache/match_data';
import { loadCourses } from './coursesLoader';
import { cachePreviousSeasonRating, getPreviousSeasonRatingFromCache } from './cache';
import { toFirebaseEventTime } from './formatting';

import {
    BASE_RATING,
} from './const';

import {
    calculateSummaryData,
    normalizeHistoryData
} from './transformers/history_data';

import {
    generateHeadToHeadRecords,
    generatedMatchSummaryData
} from './transformers/match_data';

import {
    generateHoleData,
    generateHoleSummaryData,
    generateCourseSummaryData
} from './transformers/hole_data';

import {
    generateCardData,
    generateCardSummaryData
} from './transformers/card_data';

import { DataContextType } from '../data_types/interfaces';
import {
    CardData,
    CardSummaryData,
    HoleData,
    HoleSummaryData,
    CourseSummaryData,
    SummaryData,
    HeadToHeadRecords,
    NormalizedGameData,
    NormalizedMatchData,
    MatchSummaryData,
    EventFormData,
    EventModel,
    PlainObject,
    EventPendingModel
} from '../data_types/types';

import { Course } from '../data_types/courseTypes';
import { EventLocation, EventTransitionModel, InviteRoster, ScoreExportFormat } from '../data_types/eventTypes';

export const DataContext = createContext<DataContextType | undefined>(undefined);

const adminUserIds = ['dxynPrvMU3NOgvDL6SoALonHuKv2', 'oJsPAIy7xWMF6PHSH0Yjt9ra0CI2', 'yw67eNFm0AW2ixv7T466EatesjV2', 'OLGrOwV2jaZOe7AKrGSLKNJUeyn1', 'TgnfM1y2HwWSTxlTO6lP7rfIzsi1'];

interface Props {
    children: ReactNode;
    userId: string;
}

export function DataProvider({ children, userId }: Props) {
    const [historyData, setHistoryData] = useState<NormalizedGameData[]>([]);
    const [playerSeasonData, setPlayerSeasonData] = useState(null);
    const [previousSeasonRating, setPreviousSeasonRating] = useState<number | null>(null);
    const [playerData, setPlayerData] = useState(null);
    const [seasonIndex, setSeasonIndex] = useState(0);
    const [currentSeason, setSeason] = useState('');
    const [previousSeason, setPreviousSeason] = useState('');
    const [summaryData, setSummaryData] = useState<SummaryData | object>({});
    const [matchData, setMatchData] = useState<NormalizedMatchData[]>([]);
    const [matchSummaryData, setMatchSummaryData] = useState<MatchSummaryData | object>({});
    const [headToHeadRecords, setHeadToHeadRecords] = useState<HeadToHeadRecords | object>({});
    const [holeData, setHoleData] = useState<HoleData[]>([]);
    const [holeSummaryData, setHoleSummaryData] = useState<HoleSummaryData | object>({});
    const [courseSummaryData, setCourseSummaryData] = useState<CourseSummaryData | object>({});
    const [cardData, setCardData] = useState<CardData[]>([]);
    const [cardSummaryData, setCardSummaryData] = useState<CardSummaryData | object>({});
    const [deleteConfirmed, setDeleteConfirmed] = useState<boolean>(false);
    const [isClubMember, setIsClubMember] = useState<boolean>(false);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [isCreator, setIsCreator] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingCourses, setLoadingCourses] = useState<boolean>(true);
    const [courses, setCourses] = useState<Course[]>([]);
    const [pendingEvents, setPendingEvents] = useState<EventFormData | PlainObject>({});
    const [privateEvents, setPrivateEvents] = useState<EventFormData | PlainObject>({});
    const [publicEvents, setPublicEvents] = useState<EventFormData | PlainObject>({});
    const [archivedEvents, setArchivedEvents] = useState<EventFormData | PlainObject>({});
    const [inviteEvents, setInviteEvents] = useState<EventFormData | PlainObject>({});
    const [inviteRosters, setInviteRosters] = useState<{ [eventId: string]: InviteRoster }>({});

    useEffect(() => {
        const db = getDatabase();
        const seasonIndexRef = ref(db, '/ranked/currentSeasonIndex');
        const seasonsListRef = ref(db, '/ranked/seasonsList');

        onValue(seasonIndexRef, (snapshot) => {
            const seasonIndex = snapshot.val();

            setSeasonIndex(seasonIndex);

            onValue(seasonsListRef, (snapshot) => {
                const seasonsList = snapshot.val();
                const currentSeason = seasonsList[seasonIndex];

                setSeason(currentSeason);

                const previousSeason = seasonsList[seasonIndex - 1];

                setPreviousSeason(previousSeason);
            });
        });
    }, [userId]);

    useEffect(() => {
        if (currentSeason !== '') {
            const db = getDatabase();
            const playerSeasonRef = ref(db, `/ranked/seasonData/${currentSeason}/players/${userId}`);

            onValue(playerSeasonRef, (snapshot) => {
                setPlayerSeasonData(snapshot.val());
            });
        }
    }, [currentSeason, userId]);

    useEffect(() => {
        if (playerSeasonData && seasonIndex && currentSeason !== '' && previousSeasonRating) {
            const db = getDatabase();
            const historyRef = ref(db, `/ranked/seasonData/${currentSeason}/history/${userId}`);

            onValue(historyRef, (snapshot) => {
                const historyData = snapshot.val();

                if (historyData) {
                    const normalizedHistoryData = normalizeHistoryData(
                        seasonIndex,
                        previousSeasonRating,
                        historyData
                    );

                    setHistoryData(normalizedHistoryData);
                    setSummaryData(calculateSummaryData(normalizedHistoryData, seasonIndex, playerSeasonData))

                    loadMatchData(
                        normalizedHistoryData,
                        currentSeason,
                        userId
                    ).then((data) => {
                        const _headToHeadRecords = generateHeadToHeadRecords(userId, data);
                        const _matchSummaryData = generatedMatchSummaryData(_headToHeadRecords);
                        const _cardData = generateCardData(userId, data);
                        const _cardSummaryData = generateCardSummaryData(_cardData, _headToHeadRecords);

                        setMatchData(data);
                        setCardData(_cardData);
                        setCardSummaryData(_cardSummaryData);
                        setHeadToHeadRecords(_headToHeadRecords);
                        setMatchSummaryData(_matchSummaryData);
                    });
                }
            });
        }
    }, [seasonIndex, currentSeason, userId, previousSeasonRating, playerSeasonData]);

    useEffect(() => {
        if (previousSeason !== '') {
            const cachedPreviousSeasonRating = getPreviousSeasonRatingFromCache(previousSeason, userId);

            if (cachedPreviousSeasonRating) {
                // console.log('reading previous season rating from cache');
                setPreviousSeasonRating(cachedPreviousSeasonRating);

            } else {
                const db = getDatabase();
                const playerPreviousSeasonRef = ref(db, `/ranked/seasonData/${previousSeason}/players/${userId}`);

                // console.log('reading previous season rating from database');

                onValue(playerPreviousSeasonRef, (snapshot) => {
                    const previousSeasonData = snapshot.val();
                    const previousSeasonRating = get(previousSeasonData, 'rating', BASE_RATING);

                    setPreviousSeasonRating(previousSeasonRating);
                    cachePreviousSeasonRating(previousSeason, userId, previousSeasonRating);
                });
            }
        }
    }, [previousSeason, userId]);

    useEffect(() => {
        if (userId !== '') {
            const db = getDatabase();
            const playerDataRef = ref(db, `/playerData/${userId}`);

            onValue(playerDataRef, (snapshot) => {
                setPlayerData(snapshot.val());
            });
        }
    }, [userId]);

    useEffect(() => {
        if (userId !== '') {
            const db = getDatabase();
            const subscriptionRef = ref(db, `/playerData/${userId}/playerSubscriptionData/isSubscribed`);

            onValue(subscriptionRef, (snapshot) => {
                setIsClubMember(snapshot.val());
                // setIsClubMember(false);
            });
        }
    }, [userId]);

    useEffect(() => {
        if (userId !== '') {
            const isAdminValue = adminUserIds.includes(userId);
            console.log(`DataProvider: isAdmin set to ${isAdminValue} for userId ${userId}`);
            setIsAdmin(isAdminValue);
        }
    }, [userId]);

    useEffect(() => {
        if (userId !== '') {
            const db = getDatabase();
            const creatorRef = ref(db, `/playerData/${userId}/isCreator`);

            onValue(creatorRef, (snapshot) => {
                console.log(`DataProvider: isCreator set to ${snapshot.val()} for userId ${userId}`);
                setIsCreator(snapshot.val());
                setLoading(false);
            });
        }
    }, [userId]);

    useEffect(() => {
        if (!isEmpty(matchData)) {
            const db = getDatabase();
            const holdDiffRef = ref(db, `/holeDifficulties`);

            onValue(holdDiffRef, (snapshot) => {
                const _holeData = generateHoleData(userId, matchData, snapshot.val());
                const _holeSummaryData = generateHoleSummaryData(_holeData);
                const _courseSummaryData = generateCourseSummaryData(_holeData);

                setHoleData(_holeData);
                setHoleSummaryData(_holeSummaryData);
                setCourseSummaryData(_courseSummaryData);
            });
        }
    }, [userId, matchData]);

    // gamedata/ in Storage is only readable by signed-in users, so wait for the user
    useEffect(() => {
        if (userId === '') return;

        let cancelled = false;

        loadCourses().then((loadedCourses) => {
            if (cancelled) return;
            setCourses(loadedCourses);
            setLoadingCourses(false);
        });

        return () => {
            cancelled = true;
        };
    }, [userId]);

    useEffect(() => {
        const db = getDatabase();
        const eventsRef = ref(db, `/events/pending`);

        onValue(eventsRef, (snapshot) => {
            setPendingEvents(snapshot.val() || {});
        });
    }, []);

    useEffect(() => {
        const db = getDatabase();
        const eventsRef = ref(db, `/events/private`);

        onValue(eventsRef, (snapshot) => {
            setPrivateEvents(snapshot.val() || {});
        });
    }, []);

    useEffect(() => {
        const db = getDatabase();
        const eventsRef = ref(db, `/events/public`);

        onValue(eventsRef, (snapshot) => {
            setPublicEvents(snapshot.val() || {});
        });
    }, []);

    useEffect(() => {
        const db = getDatabase();
        const eventsRef = ref(db, `/events/archive`);

        onValue(eventsRef, (snapshot) => {
            setArchivedEvents(snapshot.val() || {});
        });
    }, []);

    useEffect(() => {
        const db = getDatabase();
        const eventsRef = ref(db, `/events/invite`);

        onValue(eventsRef, (snapshot) => {
            setInviteEvents(snapshot.val() || {});
        }, (error) => {
            console.error('Could not read invite events:', error);
        });
    }, []);

    // Who is invited to each Invite event; only admins manage invites
    useEffect(() => {
        if (!isAdmin) return;

        const db = getDatabase();
        const rostersRef = ref(db, `/events/inviteAccess`);

        return onValue(rostersRef, (snapshot) => {
            setInviteRosters(snapshot.val() || {});
        }, (error) => {
            console.error('Could not read invite rosters:', error);
        });
    }, [isAdmin]);

    // useEffect(() => {
    //     const storage = getStorage();
    //     const stampsRef = storageRef(storage);
    //     console.log(stampsRef);

    //     listAll(stampsRef)
    //         .then((res)=> {
    //             res.items.forEach((itemRef)=> {
    //                 console.log(itemRef);
    //             });
    //         })
    //         .catch((err)=> {
    //             console.error(err);
    //         });
    // }, []);

    const deletePlayerData = async () => {
        const db = getDatabase();

        if (!userId || userId === null) {
            console.error('Invalid user or not logged in');
            return;
        }

        if (!playerData) {
            console.error('Player data is not valid');
            return;
        }

        const playerDataRef = ref(db, `/playerData/${userId}`);

        await remove(playerDataRef);

        if (currentSeason !== '') {

            const playerSeasonRef = ref(db, `/ranked/seasonData/${currentSeason}/players/${userId}`);
            const playerHistoryRef = ref(db, `/ranked/seasonData/${currentSeason}/history/${userId}`);

            await remove(playerSeasonRef);
            await remove(playerHistoryRef);
        }
    };

    const addEventData = async (eventId: string, eventData: any) => {
        const db = getDatabase();

        // Ensure the user is authenticated and is either an admin or a creator
        console.log(`DataProvider: userId is ${userId}, isAdmin is ${isAdmin}, isCreator is ${isCreator}`);
        if (!userId || (!isAdmin && !isCreator)) {
            throw new Error('Unauthorized: Only admin or creator users can add event data.');
        }

        const eventRef = ref(db, `/events/pending/${eventId}`);

        // Modify a specific value inside the rounds array
        const modifiedEventData = {
            ...eventData,
            eventStartTime: toFirebaseEventTime(eventData.eventStartTime),
            eventEndTime: toFirebaseEventTime(eventData.eventEndTime),
            eventInfoURL: (eventData.eventInfoURL || '').trim(),
            rounds: eventData.rounds.map((round: any) => ({
                ...round,
                courseID: '0'
            })),
        };

        try {
            await set(eventRef, modifiedEventData);
            console.log(`Event data for '${eventId}' added successfully.`);
        } catch (error) {
            console.error(`Error adding event data for '${eventId}':`, error);
            throw error;
        }
    };

    // Same writes as FireBaseEvents.InvitePlayersToEvent in the game: the event's roster plus each player's own
    // index, in one atomic update. The game deletes index entries whose event isn't in events/invite, so only
    // call this for events that are already live as Invite events.
    const invitePlayersToEvent = async (eventId: string, playerIds: string[]) => {
        if (!userId || !isAdmin) {
            throw new Error('Unauthorized: Only admin users can invite players.');
        }

        const updates: PlainObject = {};
        playerIds.forEach((playerId) => {
            updates[`events/inviteAccess/${eventId}/${playerId}`] = true;
            updates[`playerData/${playerId}/invitedEvents/${eventId}`] = true;
        });

        if (!eventId || isEmpty(updates)) return;
        await update(ref(getDatabase()), updates);
    };

    // Reverse of invitePlayersToEvent, matching FireBaseEvents.RevokeEventInvites
    const revokeEventInvites = async (eventId: string, playerIds: string[]) => {
        if (!userId || !isAdmin) {
            throw new Error('Unauthorized: Only admin users can revoke invites.');
        }

        const updates: PlainObject = {};
        playerIds.forEach((playerId) => {
            updates[`events/inviteAccess/${eventId}/${playerId}`] = null;
            updates[`playerData/${playerId}/invitedEvents/${eventId}`] = null;
        });

        if (!eventId || isEmpty(updates)) return;
        await update(ref(getDatabase()), updates);
    };

    // Player names come from the current ranked season, so players who haven't played it resolve to null
    const lookupPlayerName = async (playerId: string): Promise<string | null> => {
        if (!currentSeason || !playerId) return null;

        try {
            const snapshot = await getValue(ref(getDatabase(), `/ranked/seasonData/${currentSeason}/players/${playerId}/name`));
            return snapshot.exists() ? String(snapshot.val()) : null;
        } catch (error) {
            console.error(`Could not look up player ${playerId}:`, error);
            return null;
        }
    };

    /**
     * Moves an event from one location state to another using the EventTransitionModel.
     * This method provides a unified way to handle all event state transitions (pending, private, public, archived).
     * 
     * @param eventTransition - The event transition model containing:
     *   - eventId: The unique identifier of the event to move
     *   - source: Object with location property indicating current event state
     *   - destination: Object with location property indicating target event state
     * 
     * @throws {Error} If user is not authorized (must be admin or creator)
     * @throws {Error} If eventId is invalid or empty
     * @throws {Error} If source or destination locations are missing
     * @throws {Error} If location values are not valid EventLocation enum values
     * 
     * @returns {Promise<void>} Resolves when the event is successfully moved
     * 
     * @example
     * // Move event from pending to private
     * await moveEventToSelectedNode({
     *   eventId: 'event123',
     *   source: { location: EventLocation.PENDING },
     *   destination: { location: EventLocation.PRIVATE }
     * });
     */
    const moveEventToSelectedNode = async (eventTransition: EventTransitionModel) => {

        if (!userId || (!isAdmin && !isCreator)) {
            throw new Error('Unauthorized: Only admin or creator users can move events.');
        }

        if (!eventTransition.eventId || typeof eventTransition.eventId !== 'string' || eventTransition.eventId.trim() === '') {
            throw new Error('Invalid eventId: eventId must be a non-empty string.');
        }

        if (!eventTransition.source?.location || !eventTransition.destination?.location) {
            throw new Error('Invalid transition: source and destination locations are required.');
        }

        if (!Object.values(EventLocation).includes(eventTransition.source.location) ||
            !Object.values(EventLocation).includes(eventTransition.destination.location)) {
            throw new Error('Invalid location: must be a valid EventLocation value.');
        }

        const data = JSON.stringify(eventTransition);

        console.log(`Moving event ${eventTransition.eventId} from ${eventTransition.source.location} to ${eventTransition.destination.location} with data:`, data);

        try {
            const functions = getFunctions();
            // Use a single cloud function that handles all transitions
            const eventTransitionFunction = httpsCallable(functions, 'eventMoveToSelectedNode');
            const response = await eventTransitionFunction(data);
            console.log(`Event ${eventTransition.eventId} is successfully moved from ${eventTransition.source.location} to ${eventTransition.destination.location}:`, response.data);
        } catch (error) {
            console.error(`Error moving event ${eventTransition.eventId} from ${eventTransition.source.location} to ${eventTransition.destination.location}:`, error);
            throw error;
        }

        if (eventTransition.destination.location === EventLocation.INVITE) {
            await sendDraftInvites(eventTransition.eventId);
        }
    };

    // Once an event is live as an Invite event, invite the players saved on it while it was pending.
    // The list is then dropped from the event: events/inviteAccess is the roster from here on.
    // The saved list wins over an older roster (it was edited while pending), so anyone dropped from it is revoked.
    // Without a list the existing roster is re-invited, since the game prunes players' index entries while the
    // event is outside events/invite.
    const sendDraftInvites = async (eventId: string) => {
        const db = getDatabase();
        const draftRef = ref(db, `/events/invite/${eventId}/invitedPlayers`);
        const [draftSnapshot, rosterSnapshot] = await Promise.all([
            getValue(draftRef),
            getValue(ref(db, `/events/inviteAccess/${eventId}`)),
        ]);
        const draftPlayers: string[] | null = draftSnapshot.val();
        const rosterPlayers = Object.keys(rosterSnapshot.val() || {});

        if (draftPlayers) {
            const dropped = rosterPlayers.filter((playerId) => !draftPlayers.includes(playerId));
            await invitePlayersToEvent(eventId, draftPlayers);
            await revokeEventInvites(eventId, dropped);
            await remove(draftRef);
            console.log(`Invited ${draftPlayers.length} and revoked ${dropped.length} player(s) on event ${eventId}.`);
        } else if (!isEmpty(rosterPlayers)) {
            await invitePlayersToEvent(eventId, rosterPlayers);
            console.log(`Re-invited ${rosterPlayers.length} player(s) to event ${eventId}.`);
        }
    };

    /**
     * Sets the lock/unlock status for a specific round within an event.
     * This method allows admins and creators to control whether players can submit scores for a particular round.
     * 
     * @param eventId - The unique identifier of the event containing the round
     * @param roundNumber - The round number to lock/unlock (0-based index)
     * @param isPublic - Whether the event is public or private (affects which cloud function endpoint to use)
     * @param lockEvent - True to lock the round (prevent score submissions), false to unlock it
     * 
     * @throws {Error} If user is not authorized (must be admin or creator)
     * @throws {Error} If eventId is invalid or empty string
     * @throws {Error} If roundNumber is not a valid non-negative number
     * 
     * @returns {Promise<void>} Resolves when the round status is successfully updated
     * 
     * @example
     * // Lock round 1 for a public event
     * await eventSetRoundStatus('event123', 1, true, true);
     * 
     * @example
     * // Unlock round 0 for a private event
     * await eventSetRoundStatus('event456', 0, false, false);
     */
    const eventSetRoundStatus = async (eventId: string, roundNumber: Number, isPublic: boolean, lockEvent: boolean) => {

        if (!userId || (!isAdmin && !isCreator)) {
            throw new Error('Unauthorized: Only admin or creator users can add event data.');
        }

        if (typeof eventId !== 'string' || eventId.trim() === '') {
            throw new Error('Invalid eventId: eventId must be a non-empty string.');
        }

        if (typeof roundNumber !== 'number' || roundNumber < 0) {
            throw new Error('Invalid round: round must be a non-negative number.');
        }

        const eventModel = new EventModel(eventId, roundNumber, isPublic, lockEvent);
        const data = JSON.stringify(eventModel);

        try {
            const functions = getFunctions();
            const adminUnlockRoundSecure = httpsCallable(functions, 'eventSetRoundStatusSecure');
            const response = await adminUnlockRoundSecure(data);
            console.log(`Event ${eventModel.eventId} round is set to ${lockEvent} successfully:`, response.data);
        } catch (error) {
            console.error(`Error unlocking event round for ${eventModel.eventId}:`, error);
            throw error;
        }
    };

    /**
     * Exports event scores data to a CSV or JSON file.
     * Fetches score data from the Firebase database path: /events/scores/{eventId}
     * 
     * @param eventId - The unique identifier of the event to export scores for
     * @param eventName - The name of the event (used for filename)
     * @param format - 'csv' for the per-player summary, 'json' for the raw scores node as stored in Firebase
     * 
     * @throws {Error} If user is not authorized (must be admin or creator)
     * @throws {Error} If eventId is invalid or empty
     * 
     * @returns {Promise<{ success: boolean; error?: string }>} Result object indicating success/failure
     * 
     * @example
     * const result = await exportEventScores('event123', 'Summer Tournament');
     * if (!result.success) {
     *   console.error('Export failed:', result.error);
     * }
     */
    const exportEventScores = async (eventId: string, eventName: string, format: ScoreExportFormat = 'csv'): Promise<{ success: boolean; error?: string }> => {
        if (!userId || (!isAdmin && !isCreator)) {
            return { success: false, error: 'Unauthorized: Only admin or creator users can export event scores.' };
        }

        if (!eventId || typeof eventId !== 'string' || eventId.trim() === '') {
            return { success: false, error: 'Invalid eventId: eventId must be a non-empty string.' };
        }

        console.log(`Exporting scores for event ${eventId} (${eventName})...`);

        const db = getDatabase();
        const scoresRef = ref(db, `/events/scores/${eventId}`);

        try {
            // Fetch scores data
            const snapshot = await new Promise((resolve, reject) => {
                onValue(scoresRef, resolve, reject, { onlyOnce: true });
            });

            const scoresData = (snapshot as any).val();
            
            if (!scoresData) {
                return { success: false, error: 'NO_SCORES_FOUND' };
            }

            console.log(`Fetched scores data for event ${eventId}:`, scoresData);

            // CSV is the per-player summary; JSON is the untouched scores node
            const blob = format === 'json'
                ? new Blob([JSON.stringify(scoresData, null, 2)], { type: 'application/json;charset=utf-8;' })
                : new Blob([convertScoresToCSV(scoresData)], { type: 'text/csv;charset=utf-8;' });

            // Create and download the file
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `${eventName}_scores_${eventId}.${format}`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            console.log(`Event scores for '${eventId}' exported successfully.`);
            return { success: true };
        } catch (error) {
            console.error(`Error exporting event scores for '${eventId}':`, error);
            return { success: false, error: `Failed to export scores: ${error instanceof Error ? error.message : 'Unknown error'}` };
        }
    };

    /**
     * Converts event scores data to CSV format with player summary (one row per player).
     * Expected structure: scores/{eventId}/{playerId} = { rounds: [{ holeScores: [{ score, holePar, inReg }] }] }
     * 
     * @param scoresData - The scores data object from Firebase
     * @returns {string} CSV formatted string with player summaries
     */
    const convertScoresToCSV = (scoresData: any): string => {
        const headers = ['Player ID', 'Player Name', 'Round 1', 'Round 2', 'Round 3', 'Round 4', 'Total Score', 'Ace Counter', 'Holes Completed'];
        const rows = [headers.join(',')];

        // Helper function to calculate total score from actual hole data
        const calculateTotalScore = (playerData: any): number => {
            if (!playerData?.rounds || !Array.isArray(playerData.rounds)) {
                return 999; // Return high score for players with no valid data
            }
            
            let totalScore = 0;
            playerData.rounds.forEach((round: any) => {
                if (round.holeScores && Array.isArray(round.holeScores)) {
                    round.holeScores.forEach((holeScore: any) => {
                        const score = holeScore.score || 0;
                        const par = holeScore.holePar || 0;
                        totalScore += (score - par);
                    });
                }
            });
            return totalScore;
        };

        // Convert player data to array and sort by calculated total score (best score first)
        const sortedPlayers = Object.entries(scoresData)
            .map(([playerId, playerData]: [string, any]) => ({ 
                playerId, 
                playerData,
                calculatedScore: calculateTotalScore(playerData)
            }))
            .sort((a, b) => {
                return a.calculatedScore - b.calculatedScore; // Ascending order (lower scores are better in disc golf)
            });

        // Helper function to format score relative to par
        const formatScoreToPar = (score: number, par: number): number => {
            return score - par; // Positive = over par, Negative = under par, 0 = even par
        };

        // Helper function to format total score with proper +/- notation
        const formatTotalScore = (totalScore: number): string => {
            if (totalScore > 0) return `+${totalScore}`;
            if (totalScore < 0) return `${totalScore}`; // Already has negative sign
            return 'E'; // Even par
        };

        // Process each player to create summary row
        sortedPlayers.forEach(({ playerId, playerData, calculatedScore }) => {
            if (playerData?.rounds && Array.isArray(playerData.rounds)) {
                const playerName = playerData.rounds[0]?.playerName || 'Unknown';
                
                // Initialize round scores array (up to 4 rounds)
                const roundScores = ['', '', '', '']; // Empty strings for missing rounds
                let aceCounter = 0;
                let holesCompleted = 0;

                // Process each round
                playerData.rounds.forEach((round: any, roundIndex: number) => {
                    if (roundIndex < 4 && round.holeScores && Array.isArray(round.holeScores)) {
                        // Calculate round total relative to par
                        let roundTotalToPar = 0;
                        
                        round.holeScores.forEach((holeScore: any) => {
                            const score = holeScore.score || 0;
                            const par = holeScore.holePar || 0;
                            
                            // Calculate score relative to par for this hole
                            const scoreToPar = formatScoreToPar(score, par);
                            roundTotalToPar += scoreToPar;
                            holesCompleted++;
                            
                            // Count aces (hole-in-one) - score is 1
                            if (score === 1) {
                                aceCounter++;
                            }
                        });
                        
                        // Format round score with +/- notation
                        roundScores[roundIndex] = formatTotalScore(roundTotalToPar);
                    }
                });

                // Use calculated total score instead of sortScore from Firebase
                const formattedTotalScore = formatTotalScore(calculatedScore);

                const row = [
                    playerId,
                    `"${playerName.replace(/"/g, '""')}"`, // Escape quotes in player names
                    roundScores[0], // Round 1 total (relative to par)
                    roundScores[1], // Round 2 total (relative to par)
                    roundScores[2], // Round 3 total (relative to par)
                    roundScores[3], // Round 4 total (relative to par)
                    formattedTotalScore, // Total score relative to par with +/- notation
                    aceCounter, // Number of aces
                    holesCompleted // Total holes completed
                ];
                rows.push(row.join(','));
            }
        });

        return rows.join('\n');
    };

    // Event Set Round status, will lock or unlock a specific round for a specific event
    return (
        <DataContext.Provider
            value={{
                currentSeason,
                historyData,
                playerSeasonData,
                playerData,
                previousSeason,
                previousSeasonRating,
                seasonIndex,
                summaryData,
                matchData,
                matchSummaryData,
                headToHeadRecords,
                holeData,
                holeSummaryData,
                courseSummaryData,
                cardData,
                cardSummaryData,
                deletePlayerData,
                deleteConfirmed,
                setDeleteConfirmed,
                isClubMember,
                userId,
                isAdmin,
                isCreator,
                loading,
                addEventData,
                loadingCourses,
                courses,
                pendingEvents,
                privateEvents,
                publicEvents,
                archivedEvents,
                inviteEvents,
                inviteRosters,
                invitePlayersToEvent,
                revokeEventInvites,
                lookupPlayerName,
                moveEventToSelectedNode,
                eventSetRoundStatus,
                exportEventScores,
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export const usePlayerData = () => useContext(DataContext);
